import { useMemo, useState } from "react";
import {
  buildImageInventory,
  currentRatioLabel,
  estimateFileSize,
  imageStandards,
  parseImageDims,
  standardByKey,
} from "@/lib/panelData";
import { LockedButton, PanelBadge } from "./PanelUI";

export default function ImageInventory() {
  const inventory = useMemo(() => buildImageInventory(), []);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("all");

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return inventory.filter((asset) => {
      if (type !== "all" && asset.standardKey !== type) return false;
      if (!term) return true;
      return [asset.label, asset.page, asset.section, asset.related, asset.purpose, asset.alt]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [inventory, query, type]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
          <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base" />
          </span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar por imagem, página, seção ou produto"
            className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
          />
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-foreground-600">
            Tipo
            <select
              value={type}
              onChange={(event) => setType(event.target.value)}
              className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-800 outline-none"
            >
              <option value="all">Todos os tipos</option>
              {imageStandards.map((standard) => (
                <option key={standard.key} value={standard.key}>
                  {standard.label}
                </option>
              ))}
            </select>
          </label>
          <PanelBadge tone="primary">{filtered.length} imagens</PanelBadge>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {filtered.map((asset) => {
          const standard = standardByKey(asset.standardKey);
          const dims = parseImageDims(asset.url);
          return (
            <article
              key={asset.id}
              className="grid grid-cols-1 gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:grid-cols-[112px_1fr]"
            >
              <div className="h-[140px] w-[112px] shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-100">
                <img
                  src={asset.url}
                  alt={asset.alt}
                  title={asset.label}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-heading text-lg text-foreground-900">{asset.label}</h3>
                  <PanelBadge tone="secondary">{asset.variant}</PanelBadge>
                  <PanelBadge tone="neutral">{asset.status}</PanelBadge>
                </div>

                <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-3">
                  <div className="flex gap-2">
                    <dt className="text-foreground-500">Página:</dt>
                    <dd className="truncate text-foreground-800">{asset.page}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500">Seção:</dt>
                    <dd className="truncate text-foreground-800">{asset.section}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500">Relacionado:</dt>
                    <dd className="truncate text-foreground-800">{asset.related}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500">Finalidade:</dt>
                    <dd className="truncate text-foreground-800">{asset.purpose}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500">Dimensão atual:</dt>
                    <dd className="text-foreground-800">
                      {dims.width} × {dims.height} px
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500">Proporção atual:</dt>
                    <dd className="text-foreground-800">{currentRatioLabel(dims.width, dims.height)}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500">Recomendada:</dt>
                    <dd className="text-foreground-800">
                      {standard ? `${standard.width} × ${standard.height} px` : "—"}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500">Proporção ideal:</dt>
                    <dd className="text-foreground-800">{standard?.ratio ?? "—"}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500">Formato:</dt>
                    <dd className="text-foreground-800">WebP (recomendado) · JPG / PNG aceitos</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500">Tamanho aprox.:</dt>
                    <dd className="text-foreground-800">{estimateFileSize(dims.width, dims.height)}</dd>
                  </div>
                  <div className="flex gap-2 sm:col-span-2">
                    <dt className="text-foreground-500">Origem:</dt>
                    <dd className="truncate text-foreground-800">{asset.origin}</dd>
                  </div>
                  <div className="flex gap-2 sm:col-span-2">
                    <dt className="text-foreground-500">Texto alternativo:</dt>
                    <dd className="truncate text-foreground-800">{asset.alt}</dd>
                  </div>
                </dl>

                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <LockedButton icon="ri-image-edit-line">Substituir imagem</LockedButton>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}